import { motion, useReducedMotion } from 'framer-motion';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'formation' | 'session' | 'formateur' | 'opportunite' | 'kpi' | 'neutral';

const GRADIENTS: Record<Variant, string> = {
  formation: 'from-amber-500/10 via-orange-400/5 to-transparent',
  session: 'from-violet-500/10 via-fuchsia-400/5 to-transparent',
  formateur: 'from-cyan-500/10 via-teal-400/5 to-transparent',
  opportunite: 'from-indigo-500/10 via-violet-400/5 to-transparent',
  kpi: 'from-emerald-500/10 via-teal-400/5 to-transparent',
  neutral: 'from-slate-500/5 via-slate-400/0 to-transparent',
};

const RING: Record<Variant, string> = {
  formation: 'ring-amber-500/15',
  session: 'ring-violet-500/15',
  formateur: 'ring-cyan-500/15',
  opportunite: 'ring-indigo-500/15',
  kpi: 'ring-emerald-500/15',
  neutral: 'ring-border',
};

interface ModernCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'
> {
  variant?: Variant;
  hover?: boolean;
  selected?: boolean;
  /** Disables animations regardless of prefers-reduced-motion. */
  disableAnimation?: boolean;
  children: ReactNode;
}

export function ModernCard({
  variant = 'neutral',
  hover = true,
  selected = false,
  disableAnimation = false,
  className,
  children,
  ...rest
}: ModernCardProps): JSX.Element {
  const reduce = useReducedMotion();
  const animate = !disableAnimation && !reduce;
  return (
    <motion.div
      initial={animate ? { opacity: 0, y: 8 } : false}
      animate={animate ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      whileHover={animate && hover ? { y: -2 } : undefined}
      className={cn(
        'group relative overflow-hidden rounded-xl border border-border/60 bg-card/80 backdrop-blur',
        'shadow-sm transition-shadow duration-200',
        hover && 'hover:shadow-md hover:shadow-black/5',
        'ring-1',
        selected ? 'border-primary/60 ring-2 ring-primary/40' : RING[variant],
        className,
      )}
      {...rest}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-90',
          GRADIENTS[variant],
        )}
        aria-hidden
      />
      <div className="relative">{children}</div>
    </motion.div>
  );
}

interface ModernCardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}

export function ModernCardHeader({
  icon,
  title,
  subtitle,
  trailing,
  className,
  ...rest
}: ModernCardHeaderProps): JSX.Element {
  return (
    <div className={cn('flex items-start gap-3 p-4 pb-2', className)} {...rest}>
      {icon && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background/60 text-foreground/80 ring-1 ring-border/50">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-sm font-semibold leading-tight">{title}</div>
        {subtitle && (
          <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{subtitle}</div>
        )}
      </div>
      {trailing}
    </div>
  );
}

export function ModernCardBody({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={cn('px-4 pb-3 text-xs', className)} {...rest}>
      {children}
    </div>
  );
}

export function ModernCardFooter({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 border-t border-border/40 bg-background/30 px-4 py-2.5 text-xs',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
